import { Box, CircularProgress } from '@mui/material';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useBiweeklyReportContext } from '../../../context/BiweeklyReportContext';
import { IRRIGATION_NAME, CHART_TITLE_RIEGO } from '../../../constants/graphs';
import apiClient from '../../../requests/api/apiClient';
import {
  ConversationSchema,
  TimePointSchema,
  CultivationCyclePopulatedSchema,
  PhenologicalStateWithCycle,
  GetCultivationCyclesBySectorResponse,
  PhenologicalStateSectorResponse,
} from '../../../requests/api/apiTypes';
import { ReduxState } from '../../../types';
import { navigateToChart } from '../../../utils/helpers';
import { FetchError } from '../../../views/types';
import BaseHighchart from '../BaseHighchart';
import ChartFilter from '../ChartFilter';
import ConversationModal from '../ConversationTab/conversationModal';
import {
  ChartFiltersDouble, RecordData,
} from '../types';
import { getIrrigationChartConfig, getPointWidth } from '../utils';
import prepareData from './builder';
import useFetchApi from '../../../hooks/useFetchApi';
import { getKcColor } from '../utils/colorUtils';
import { getConversationTooltip } from '../LineCharts/utils';
import ChartConfigDialog from '../ChartConfigDialog/ChartConfigDialog';
import IrrigationConfigContent from './IrrigationConfigContent';
import useChartConfigClick from '../../../hooks/useChartConfigClick';

// To Do: Actualizar types
interface ChartDataType {
  series: { type: string; name: string; data: any[] }[];
  kcRecord: RecordData;
  irrigationRecord: RecordData;
  et0Record: RecordData;
  rainRecord: RecordData;
  cumulativeEt0Record: RecordData;
  absoluteCategories?: string[];
}

function IrrigationChart({
  chartView,
  sectorId,
  farmId,
  hideLegend = false,
  hideYValues = false,
  height = 100,
  title = CHART_TITLE_RIEGO,
  yAxisTitle = 'Kc / Et0 (mm) / Riego + Precipitaciones (mm)',
  externalModalState,
  externalSetModalState,
  externalConversations,
  externalSetConversations,
  onConversationCreated,
  marginBottom,
  labelYPosition,
}: {
  chartView: 'dashboard' | 'graphs' | 'settings' | 'fullScreen'
  sectorId: string
  farmId: string
  hideLegend?: boolean
  hideXValues?: boolean
  hideYValues?: boolean
  height?: number | string
  title?: string
  yAxisTitle?: string
  externalModalState?: {
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  };
  externalSetModalState?: React.Dispatch<React.SetStateAction<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>>;
  externalConversations?: ConversationSchema[];
  externalSetConversations?: React.Dispatch<React.SetStateAction<ConversationSchema[]>>;
  onConversationCreated?: () => void;
  marginBottom?: number;
  labelYPosition?: number;
}) {
  const [chartData, setChartData] = useState<ChartDataType>({
    series: [],
    kcRecord: {},
    irrigationRecord: {},
    et0Record: {},
    rainRecord: {},
    cumulativeEt0Record: {},
    absoluteCategories: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);

  const history = useHistory();
  const { interval } = useSelector((state: ReduxState) => state.graphsFilter);
  const { selectedSector } = useSelector((state: ReduxState) => state.currentSector);
  const { enqueueSnackbar } = useSnackbar();
  const { isBiweeklyReportView } = useBiweeklyReportContext();

  // State for cultivation cycle and phenological states
  const [selectedCycle, setSelectedCycle] = useState<CultivationCyclePopulatedSchema | null>(
    null,
  );
  const [
    phenologicalStatesByCycle,
    setPhenologicalStatesByCycle] = useState<PhenologicalStateWithCycle>({});

  const [internalModalState, setInternalModalState] = useState<{
    open: boolean;
    conversationId: string;
    timePointData: TimePointSchema;
  }>({
    open: false,
    conversationId: '',
    timePointData: {
      hour: '',
      day: '',
      month: '',
      year: '',
    },
  });
  const [internalConversations, setInternalConversations] = useState<ConversationSchema[]>([]);

  const modalState = externalModalState || internalModalState;
  const setModalState = externalSetModalState || setInternalModalState;
  const conversations = externalConversations || internalConversations;
  const setConversations = externalSetConversations || setInternalConversations;

  const { data: cultivationCyclesData } = useFetchApi<GetCultivationCyclesBySectorResponse>({
    modelEndpoint: 'cultivationCycleSectorDetail',
    modelName: 'cultivationCycleSector',
    id: sectorId,
    dependencies: [sectorId],
  });

  const { data: phenologicalStatesData } = useFetchApi<PhenologicalStateSectorResponse>({
    modelEndpoint: 'phenologicalStateSectorList',
    modelName: 'phenologicalState',
    id: sectorId,
    dependencies: [sectorId],
  });

  useEffect(() => {
    if (cultivationCyclesData && selectedSector) {
      const savedCycleId = selectedSector.irrigationConfig?.selectedCultivationCycleId;
      const savedCycle = savedCycleId
        ? cultivationCyclesData.cultivationCycles.find(
          (c: CultivationCyclePopulatedSchema) => c.id === savedCycleId,
        )
        : null;
      setSelectedCycle(savedCycle || cultivationCyclesData.cultivationCycles[0] || null);
    }
  }, [cultivationCyclesData, selectedSector]);

  useEffect(() => {
    if (phenologicalStatesData) {
      setPhenologicalStatesByCycle(phenologicalStatesData.phenologicalStates);
    }
  }, [phenologicalStatesData]);

  const getKcColorWithState = useCallback(
    (kcValue: number, date?: string) => getKcColor(
      kcValue,
      date,
      selectedCycle,
      phenologicalStatesByCycle,
    ),
    [selectedCycle, phenologicalStatesByCycle],
  );

  const columnWidth = useMemo(() => {
    if (!interval?.startDate || !interval?.endDate) {
      return 15;
    }
    return getPointWidth(interval.startDate, interval.endDate);
  }, [interval]);

  const chartName = 'riego';

  const preparedFilters: ChartFiltersDouble = useMemo(() => {
    if (!interval || !selectedSector) {
      return {
        farmId,
        startDate: '',
        endDate: '',
        sectorId: '',
        weatherZoneId: '',
        sn: '',
        telemetryType: selectedSector?.telemetryType || 'wiseconn',
      };
    }
    return {
      farmId,
      sectorId,
      startDate: interval.startDate,
      endDate: interval.endDate,
      ...(selectedSector?.weatherZoneId && { weatherZoneId: selectedSector.weatherZoneId }),
      ...(selectedSector?.telemetryType && { telemetryType: selectedSector.telemetryType }),
      ...(selectedSector?.telemetryType === 'sn' && { sn: selectedSector.sn }),
      chart: IRRIGATION_NAME,
    };
  }, [farmId, sectorId, interval, selectedSector?.weatherZoneId,
    selectedSector?.telemetryType]);

  // Memoize series data creation
  const createSeriesData = useCallback((preparedData: any) => [
    {
      type: 'column',
      name: 'Riego (mm)',
      data: preparedData.irrigation,
      yAxis: 0,
      stack: 'water',
      color: '#536e7b',
      pointWidth: columnWidth,
    },
    {
      type: 'column',
      name: 'Precipitaciones (mm)',
      data: preparedData.rain,
      yAxis: 0,
      stack: 'water',
      color: '#35a0d7',
      pointWidth: columnWidth,
    },
    {
      type: 'column',
      name: 'ETo (mm)',
      data: preparedData.et0,
      yAxis: 0,
      stack: 'et0',
      color: '#FEBF23',
      pointWidth: columnWidth,
    },
    {
      type: 'line',
      name: 'ETo entre riegos',
      data: preparedData.cumulativeEt0,
      yAxis: 0,
      color: 'rgb(228 107 56)',
      visible: false,
      pointWidth: columnWidth,
    },
  ], [interval]);

  // Memoize fetch data function
  const fetchData = useCallback(async () => {
    if (!selectedSector || !interval?.startDate || !interval?.endDate) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const preparedData = await prepareData(preparedFilters, selectedSector?.telemetryType);

      const seriesData = createSeriesData(preparedData);

      setChartData({
        series: seriesData,
        kcRecord: preparedData.kcRecord as RecordData,
        irrigationRecord: preparedData.irrigationRecord as RecordData,
        et0Record: preparedData.et0Record as RecordData,
        rainRecord: preparedData.rainRecord as RecordData,
        cumulativeEt0Record: preparedData.cumulativeEt0Record as RecordData,
        absoluteCategories: preparedData.absoluteCategories as string[],
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [preparedFilters, interval, selectedSector, sectorId, createSeriesData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const customTooltip = useMemo(() => ({
    shared: true,
    useHTML: true,
    style: {
      zIndex: 9999,
      position: 'absolute',
    },
    formatter(this: Highcharts.TooltipFormatterContextObject) {
      if (this.series.name === 'Comentarios') {
        return getConversationTooltip(this, conversations);
      }
      const pointDate = this.points?.[0]?.point.custom?.date || '';
      const header = '<div style="font-size: 14px; font-weight: bold; margin-bottom: 8px;">Resumen</div>';
      const subHeader = `<div style="font-size: 12px; margin-bottom: 8px;">${dayjs(this.x).format('MMM DD, YYYY')}</br></div>`;
      const kcValue = chartData.kcRecord[pointDate as any];
      const irrigationValue = chartData.irrigationRecord[pointDate as any];
      const et0Value = chartData.et0Record[pointDate as any];
      const rainValue = chartData.rainRecord[pointDate as any];
      const cumulativeEt0Value = chartData.cumulativeEt0Record[pointDate as any];
      const kcColor = getKcColorWithState(Number(kcValue), pointDate);
      const kcLine = `<div style="margin: 4px 0"><span style="color:${kcColor}">\u25CF</span> Kc: <b>${Number(kcValue).toFixed(2)}</b></div>`;
      const irrigationLine = `<div style="margin: 4px 0"><span style="color:#536e7b">\u25CF</span> Riego (mm): <b>${Number(irrigationValue).toFixed(3)}</b></div>`;
      const et0Line = `<div style="margin: 4px 0"><span style="color:#FEBF23">\u25CF</span> ETo (mm): <b>${Number(et0Value).toFixed(2)}</b></div>`;
      const rainLine = `<div style="margin: 4px 0"><span style="color:#35a0d7">\u25CF</span> Precipitaciones (mm): <b>${Number(rainValue).toFixed(2)}</b></div>`;
      const cumulativeEt0Line = `<div style="margin: 4px 0"><span style="color:rgb(228 107 56)">\u25CF</span> ETo entre riegos (mm): <b>${Number(cumulativeEt0Value).toFixed(2)}</b></div>`;
      return `<div style="padding: 8px; position: relative">${header}${subHeader}${rainLine}${et0Line}${irrigationLine}${cumulativeEt0Line}${kcLine}</div>`;
    },
  }), [chartData, getKcColorWithState]);

  const handleConfigClick = useChartConfigClick({
    chartView,
    route: chartName,
    farmId,
    sectorId,
    onFullScreen: () => setConfigDialogOpen(true),
  });

  const handleFullScreenClick = useCallback(() => {
    navigateToChart(`fullScreen/${chartName}`, history, {
      farmId,
      sectorId,
    });
  }, [history, farmId, sectorId]);

  useEffect(() => {
    if (!externalConversations) {
      const loadConversations = async () => {
        try {
          const { data } = await apiClient.conversation.conversationDetail(
            sectorId,
            IRRIGATION_NAME,
          );
          setInternalConversations(data.conversations);
        } catch (err) {
          const { error } = err as FetchError;
          enqueueSnackbar(error.message, { variant: 'error' });
        }
      };

      loadConversations();
    }
  }, [sectorId, externalConversations]);

  const chartConfig = useMemo(() => interval && getIrrigationChartConfig(
    chartData,
    height,
    dayjs(interval?.startDate).valueOf(),
    dayjs(interval?.endDate).valueOf(),
    false,
    undefined,
    isBiweeklyReportView,
    getKcColorWithState,
    marginBottom,
    labelYPosition,
  ), [
    interval,
    chartData.absoluteCategories,
    isBiweeklyReportView,
    sectorId,
    getKcColorWithState,
    marginBottom,
    labelYPosition,
  ]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={height}>
        <CircularProgress />
      </Box>
    );
  }
  return (
    <>
      <Box>
        <BaseHighchart
          chartView={chartView}
          sectorId={sectorId}
          filter={chartView === 'settings' ? <ChartFilter sectorId={sectorId} /> : undefined}
          title={title}
          categories={chartData.absoluteCategories}
          yAxisTitle={yAxisTitle}
          series={chartData.series as Highcharts.SeriesOptionsType[]}
          tooltip={customTooltip}
          chartConfig={chartConfig || {}}
          hideYValues={hideYValues}
          legend={{ enabled: !hideLegend }}
          setModalState={setModalState}
          conversations={conversations}
          absoluteCategories={chartData.absoluteCategories}
          onConfigClick={handleConfigClick}
          onFullScreenClick={handleFullScreenClick}
          height={height}
          zoomSync={true}
          autoAdjustYAxis={true}
          interval={interval}
          graphName={IRRIGATION_NAME}
        />
      </Box>
      <ConversationModal
        modalState={modalState}
        setModalState={setModalState}
        farmId={farmId}
        sectorId={sectorId}
        chart={IRRIGATION_NAME}
        setConversationsFunction={setConversations}
        onConversationCreated={onConversationCreated}
      />
      <ChartConfigDialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        title="Configuración · Riego"
      >
        <IrrigationConfigContent sectorId={sectorId} />
      </ChartConfigDialog>
    </>
  );
}

export default IrrigationChart;
